import { loadChannelConfig } from './config.mappers';
import { LandingPageConfigQuery } from 'generated/graphql';
import { toRawImgSrc, toRawVideoSrc } from './helpers';
import { defaultReviews, defaultFaqs } from './helpers';
import {
  BioSection as IBioSection,
  EmailCaptureSection as IEmailCaptureSection,
  FAQsSection as IFaqsSection,
  FeaturesNewSection as IFeaturesNewSection,
  IntroductionSection as IIntroductionSection,
  LogosSection as ILogosSection,
  SectionInputOf,
  TestimonialsSection as ITestimonialsSection,
} from '@playbook-technologies/common-schemas/lib/catalog';
import { compact } from 'lodash';
import { DEFAULT_LOGOS } from './Logos';
import { DEFAULT_FEATURES } from './FeaturesNewSection';
import { BusinessModel } from '@playbook-technologies/common-enums';
import {
  ChannelPersistedConfigInput as BaseLandingPagesBuilderSchema,
  SubscriptionCarouselSection as ISubscriptionCarouselSection,
} from '@playbook-technologies/common-schemas/lib/catalog';
import {
  IOffersCarouselSection,
  ISection,
  OfferData,
  SubOfferData,
} from './types';
import { toSelectableOffers } from './offers';

type LandingPageBuilderConfig = Omit<
  BaseLandingPagesBuilderSchema,
  'sections'
> & {
  sections: ISection[];
};

export function loadPageConfig(
  {
    creator,
    bundle,
  }: Pick<LandingPageConfigQuery, 'creator'> & { bundle: SubOfferData | null },
  serverConfig: LandingPageBuilderConfig | null,
  {
    primaryColor,
    colors,
    otpOffers = [],
  }: { primaryColor: string; colors: string[]; otpOffers?: OfferData[] },
): LandingPageBuilderConfig {
  if (!creator) {
    throw new Error('No influencer passed to loadPageConfig()');
  }

  if (serverConfig) return loadChannelConfig(serverConfig);

  const isLegacyCreator = creator.businessModel === BusinessModel.Legacy;
  const programsAreOnlyOTP = creator.publishedPrograms.every(
    (p) => p.accessType === 'exclusive',
  );
  const hasOnlyOTP = isLegacyCreator
    ? programsAreOnlyOTP
    : creator.publishedPrograms.length > 0 && programsAreOnlyOTP;
  const selectableOtpOffers = toSelectableOffers(otpOffers);
  const hasSubscriptionOffer = !!bundle?.id;

  const paidProgramsConfig: SectionInputOf<'PaidProgramsList'> = {
    enabled: true,
    type: 'PaidProgramsList',
    title: 'One-Time Purchases',
    subtitle: '',
    bgColor: colors[2],
    colorIndex: 2,
  };

  const subscriptionCarouselConfig: ISubscriptionCarouselSection = {
    enabled: hasSubscriptionOffer,
    type: 'SubscriptionCarousel',
    title: 'Subscription content carousel',
    subtitle: '',
    bgColor: colors[2],
    colorIndex: 2,
    offerId: bundle?.id || 0,
    programIds: [],
    communityFeatures: [],
    extrasIds: [],
  };

  const programsConfig: SectionInputOf<'ProgramsList'> = {
    enabled: true,
    type: 'ProgramsList',
    title: 'Included in subscription',
    subtitle: '',
    bgColor: colors[2],
    colorIndex: 2,
  };

  const offersCarouselConfig: IOffersCarouselSection | null =
    selectableOtpOffers.length > 0
      ? {
          enabled: true,
          type: 'OffersCarousel',
          title: 'Offers',
          subtitle: '',
          bgColor: colors[2],
          colorIndex: 2,
          offerIds: selectableOtpOffers.map((offer) => offer.id),
        }
      : null;

  let productsSection: ISection;
  if (!isLegacyCreator) {
    productsSection = hasSubscriptionOffer
      ? subscriptionCarouselConfig
      : offersCarouselConfig || subscriptionCarouselConfig;
  } else if (hasOnlyOTP) {
    productsSection = paidProgramsConfig;
  } else {
    productsSection = programsConfig;
  }

  let additionalPurchasableSection: ISection | null;
  if (!isLegacyCreator) {
    additionalPurchasableSection = hasSubscriptionOffer
      ? offersCarouselConfig
      : null;
  } else if (hasOnlyOTP) {
    additionalPurchasableSection = null;
  } else {
    additionalPurchasableSection = paidProgramsConfig;
  }

  const sections: ISection[] = compact([
    {
      enabled: true,
      type: 'Introduction',
      title: '',
      subtitle: '',
      bullets: [
        { text: 'Workout logging, scheduling, and more' },
        { text: 'Videos for each exercise' },
        { text: 'One of the best rated apps (⭐️4.9/5)' },
      ],
      coverImage: toRawImgSrc(creator.mediaCover) as string,
      introVideo: toRawVideoSrc(creator.mediaWelcome) as string,
      colorIndex: 1,
      bgColor: colors[1],
    } as IIntroductionSection,
    {
      enabled: true,
      type: 'Logos',
      bgColor: colors[2],
      colorIndex: 2,
      logos: DEFAULT_LOGOS,
    } as ILogosSection,
    {
      type: 'EmailCapture',
      enabled: false,
      bgColor: colors[2],
      colorIndex: 2,
      title: '',
      description: '',
      displayAsPopup: true,
    } as IEmailCaptureSection,
    productsSection,
    {
      enabled: true,
      type: 'Reviews',
      reviews: [...defaultReviews],
      bgColor: colors[1],
      colorIndex: 1,
    },
    additionalPurchasableSection,
    {
      type: 'ProgramsHighlight',
      enabled: false,
      title: 'Programs Highlight',
      bgColor: colors[0],
      colorIndex: 0,
      desc: '',
      programIds: [],
      bullets: [],
    } satisfies SectionInputOf<'ProgramsHighlight'>,
    {
      type: 'FeaturedBenefit',
      enabled: false,
      title: 'Featured Benefit',
      bgColor: colors[1],
      colorIndex: 1,
      bullets: [],
      coverImage: '',
    } satisfies SectionInputOf<'FeaturedBenefit'>,
    {
      enabled: true,
      type: 'Bio',
      title: creator.bio || 'Online personal trainer',
      desc: creator.messageProfile,
      coverImage: toRawImgSrc(creator.mediaAvatar) as string,
      bgColor: colors[0],
      colorIndex: 0,
      introVideo: null,
    } as IBioSection,
    {
      enabled: false,
      type: 'Testimonials',
      title: 'Need to see it to believe it?',
      testimonials: [],
      bgColor: colors[0],
      colorIndex: 0,
    } as ITestimonialsSection,
    {
      enabled: true,
      type: 'FeaturesNew',
      title: 'Program Features',
      bgColor: colors[1],
      colorIndex: 1,
      features: DEFAULT_FEATURES,
    } as IFeaturesNewSection,
    {
      enabled: true,
      type: 'FAQs',
      title: 'Frequently asked questions',
      faqs: defaultFaqs,
      bgColor: colors[2],
      colorIndex: 2,
    } as IFaqsSection,
  ]);

  const data: LandingPageBuilderConfig = {
    branding: {
      primaryColor,
      logo: null,
    },
    sections: sections,
  };

  return data;
}
